import { Button } from "@hub/components/ui/button";
import { Input } from "@hub/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@hub/components/ui/select";
import { ReleaseItems } from "@hub/ui/ReleaseItems";
import { useReleases } from "@hub/ui/use-releases";
import type React from "react";
import { useEffect, useState } from "react";
import { ApiError, apiFetch } from "./api";

interface OrgChoice {
  id: string;
  slug: string;
  name: string;
  channel: string;
  pinnedVersion: string | null;
}

interface ChannelVersions {
  early: { version: string | null };
  stable: { version: string | null };
}

export function orgFormError(orgId: string): string | null {
  return orgId.trim() ? null : "Pick the organization this person belongs to";
}

/** The version a new box starts on: the org pin, else its channel's current version. */
export function defaultVersionFor(
  org: { channel: string; pinnedVersion: string | null } | null,
  channels: ChannelVersions | null,
): string {
  if (!org) return "";
  if (org.pinnedVersion) return org.pinnedVersion;
  const channel = org.channel === "stable" ? channels?.stable : channels?.early;
  return channel?.version ?? "";
}

/**
 * The org `?org=<slug>` names. Nymbl only when the parameter is absent; an
 * unknown slug leaves the org empty so the operator has to pick one on purpose
 * rather than silently creating a box in Nymbl.
 */
export function orgIdForSlug(orgs: { id: string; slug: string }[], slug: string | null): string {
  const wanted = slug ?? "nymbl";
  return orgs.find((o) => o.slug === wanted)?.id ?? "";
}

/**
 * The Version field's next value: whatever the operator picked once they have
 * touched it, otherwise the chosen org's default. The server applies the org's
 * pin or channel version on create anyway (Task 6), so following the org here
 * keeps the form honest about what will happen.
 */
export function nextVersion(touched: boolean, current: string, orgDefault: string): string {
  return touched ? current : orgDefault;
}

interface CreatedInstance {
  id: string;
  slug: string;
  status: string;
}

/**
 * Mirrors validateNewInstance's own rule at this client layer (see the
 * onSubmit doc below), plus the one rule the server has no way to enforce:
 * "register an existing instance" means the operator is asserting they
 * already have a box, so the hash is mandatory whenever that disclosure is
 * open — independent of `kind`. Leaving Kind at its "helm" default and
 * submitting with an empty hash used to fall through to `!hash &&
 * !desiredVersion`, which passed as soon as a version was picked, and the
 * server — having no concept of "the operator meant to register", only
 * presence or absence of the hash — provisioned a fresh, billable instance.
 *
 * Exported so this can be exercised without rendering the component: this
 * repo has no React component-rendering test setup (no jsdom/happy-dom, no
 * @testing-library/react), so a real DOM-level test of the form's `required`
 * attribute isn't available here.
 */
export function agentFormError(
  registerExisting: boolean,
  agentTokenHash: string,
  desiredVersion: string,
): string | null {
  const hash = registerExisting ? agentTokenHash.trim().toLowerCase() : "";
  if (registerExisting && !hash) {
    return "Enter the agent token hash to register an existing instance";
  }
  if (!hash && !desiredVersion) {
    return "Pick a version to provision";
  }
  return null;
}

/**
 * Where to send the operator after a successful create. Pure and exported
 * for the same reason as `agentFormError`: this repo has no React
 * component-rendering test setup, so the actual decision `onSubmit` makes is
 * pulled out here rather than left untestable inside the handler.
 *
 * I3: a `pending` row (the hub will provision it) whose kick-off `/provision`
 * POST failed used to navigate on regardless, having only `console.error`'d
 * the failure — nobody watches that console, and the row then sits at
 * `pending`, a status `ProvisioningProgress` never polled and had no action
 * for. `provisionKickoffFailed` carries that failure into the query string so
 * the detail page can surface it, and `ProvisioningProgress` itself now
 * offers a Provision action for `pending` regardless of how the operator got
 * there — see this task's own fix there.
 */
export function postCreateRedirect(id: string, provisionKickoffFailed: boolean): string {
  return provisionKickoffFailed ? `/instances/${id}?provisionKickoffFailed=1` : `/instances/${id}`;
}

/** The only sizes the form offers — mirrors @hub/routes/instances'
 *  INSTANCE_TYPES. Not imported from there: that module pulls in `@hub/db`
 *  (the lazy Proxy), and this is a browser bundle with no database at all. */
const INSTANCE_TYPES = ["t4g.small", "t4g.medium", "t4g.large"] as const;
const DEFAULT_INSTANCE_TYPE = "t4g.medium";

const INSTANCE_TYPE_DESCRIPTION: Record<(typeof INSTANCE_TYPES)[number], string> = {
  "t4g.small": "2 vCPU, 2 GB — light or occasional use",
  "t4g.medium": "2 vCPU, 4 GB — comfortable for one person (default)",
  "t4g.large": "2 vCPU, 8 GB — many concurrent sessions",
};

/**
 * Registers an instance in the hub, then either has the HUB provision it
 * (the normal path — a fresh box on `<slug>.helm.nymbl.app`) or, behind the
 * "Register an existing instance" disclosure, records one already running
 * elsewhere (a laptop install or a break-glass `provision-instance.sh` box).
 *
 * The two paths are told apart by `agentTokenHash`, exactly as
 * `@hub/routes/instances`' own `validateNewInstance`/`normaliseNewInstance`
 * tell them apart: present means "already provisioned, just register it"
 * (lands `status='running'`); absent means the hub will launch it itself
 * (lands `status='pending'`, and only THEN does this page call `/provision`).
 * Calling `/provision` on an already-running row would just be refused
 * (409) — so it is only ever called for the row the hub actually owns.
 */
export function AddInstancePage() {
  const [slug, setSlug] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [desiredVersion, setDesiredVersion] = useState("");
  const [instanceType, setInstanceType] = useState<string>(DEFAULT_INSTANCE_TYPE);
  const { releases, releasesNote } = useReleases();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [versionTouched, setVersionTouched] = useState(false);
  const [registerExisting, setRegisterExisting] = useState(false);
  const [kind, setKind] = useState("helm");
  const [agentTokenHash, setAgentTokenHash] = useState("");
  const [orgs, setOrgs] = useState<OrgChoice[]>([]);
  const [channels, setChannels] = useState<ChannelVersions | null>(null);
  const [orgId, setOrgId] = useState("");

  useEffect(() => {
    Promise.all([
      apiFetch<{ data: OrgChoice[] }>("/api/orgs"),
      apiFetch<{ data: ChannelVersions }>("/api/channels"),
    ])
      .then(([o, c]) => {
        setOrgs(o.data);
        setChannels(c.data);
        setOrgId(orgIdForSlug(o.data, new URLSearchParams(window.location.search).get("org")));
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "failed to load organizations");
      });
  }, []);

  // The version follows the org until the operator picks one themselves.
  useEffect(() => {
    const org = orgs.find((o) => o.id === orgId) ?? null;
    const orgDefault = defaultVersionFor(org, channels);
    setDesiredVersion((current) => nextVersion(versionTouched, current, orgDefault));
  }, [orgs, orgId, channels, versionTouched]);

  const isLocal = registerExisting && kind === "local";
  const normalizedSlug = slug.trim().toLowerCase();
  // Only meaningful for a helm row; a local row's host is assigned by the
  // server and never reaches this form.
  const host = normalizedSlug ? `${normalizedSlug}.helm.nymbl.app` : "";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const hash = registerExisting ? agentTokenHash.trim().toLowerCase() : "";
    // See agentFormError's doc above — this is the guard against the
    // register-existing footgun, not just the `required` attribute below.
    const validationError =
      orgFormError(orgId) ?? agentFormError(registerExisting, agentTokenHash, desiredVersion);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = await apiFetch<{ data: CreatedInstance }>("/api/instances", {
        method: "POST",
        body: JSON.stringify({
          orgId,
          slug: normalizedSlug,
          kind: registerExisting ? kind : "helm",
          host: isLocal ? undefined : host,
          ownerName: ownerName.trim() || null,
          ownerEmail: ownerEmail.trim(),
          desiredVersion: desiredVersion || undefined,
          instanceType,
          agentTokenHash: hash || undefined,
        }),
      });
      const created = body.data;

      // Only a row the hub itself will provision lands `pending` (see the
      // module doc above) — a registered row is already live and calling
      // `/provision` on it would just be a refused, pointless round trip.
      let provisionKickoffFailed = false;
      if (created.status === "pending") {
        try {
          await apiFetch(`/api/instances/${created.id}/provision`, { method: "POST" });
        } catch (provisionErr) {
          // The row exists either way — surface this on the detail page
          // (postCreateRedirect) rather than only logging it to a console
          // nobody is watching (I3). The Provision action there recovers it.
          console.error("failed to start provisioning:", provisionErr);
          provisionKickoffFailed = true;
        }
      }

      window.location.href = postCreateRedirect(created.id, provisionKickoffFailed);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "failed to create instance");
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full">
      <h1 className="mb-1 font-display text-2xl font-semibold tracking-tight">Add instance</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        The hub launches and configures the box itself — pick a version and a size below and follow
        along on the next page.
      </p>

      <form
        onSubmit={onSubmit}
        className="flex max-w-3xl flex-col gap-4 rounded-xl border border-border bg-card p-6"
      >
        <Field label="Organization" htmlFor="org">
          {/* Radix reports "" when the options arrive after the value is set; a
              person can never pick "nothing", so an empty change is not a choice. */}
          <Select value={orgId} onValueChange={(v) => v && setOrgId(v)}>
            <SelectTrigger id="org" className="w-full max-w-md">
              <SelectValue placeholder="Pick an organization" />
            </SelectTrigger>
            <SelectContent>
              {orgs.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">
            The box is created with this organization's settings and starts on its channel's
            version.
          </p>
        </Field>

        <Field label="Slug" htmlFor="slug">
          <Input
            id="slug"
            className="max-w-md"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="morgan"
            required
          />
        </Field>

        {!isLocal && (
          <Field label="Host" htmlFor="host">
            <Input id="host" className="max-w-md" value={host} readOnly disabled placeholder="—" />
            <p className="mt-1 text-xs text-muted-foreground">
              Always {"<slug>"}.helm.nymbl.app — derived from slug, not editable.
            </p>
          </Field>
        )}

        <Field label="Owner name" htmlFor="owner-name">
          <Input
            id="owner-name"
            className="max-w-md"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
          />
        </Field>

        <Field label="Owner email" htmlFor="owner-email">
          <Input
            id="owner-email"
            className="max-w-md"
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            required
          />
        </Field>

        <Field label="Version" htmlFor="version">
          <Select
            value={desiredVersion}
            onValueChange={(v) => {
              // Radix reports "" when the releases arrive after the value was
              // set; that is not the operator choosing anything.
              if (!v) return;
              setVersionTouched(true);
              setDesiredVersion(v);
            }}
          >
            <SelectTrigger id="version" className="w-full max-w-md">
              <SelectValue placeholder="Pick a version" />
            </SelectTrigger>
            <SelectContent>
              <ReleaseItems releases={releases} />
            </SelectContent>
          </Select>
          {releasesNote ? (
            <p className="mt-1 text-xs text-muted-foreground">{releasesNote}</p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              Becomes <code>APP_VERSION</code> on the box.
              {isLocal ? " Optional for a registered instance." : ""}
            </p>
          )}
        </Field>

        <Field label="Instance size" htmlFor="instance-type">
          <Select value={instanceType} onValueChange={setInstanceType} disabled={registerExisting}>
            <SelectTrigger id="instance-type" className="w-full max-w-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INSTANCE_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t} — {INSTANCE_TYPE_DESCRIPTION[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {registerExisting ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Not applicable — the hub never launches a registered instance.
            </p>
          ) : null}
        </Field>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <div className="flex items-center gap-3 pt-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create instance"}
          </Button>
          <Button asChild variant="ghost">
            <a href="/">Cancel</a>
          </Button>
        </div>

        <div className="mt-2 border-t border-border pt-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="px-0 text-muted-foreground hover:text-foreground"
            onClick={() => setRegisterExisting((v) => !v)}
            aria-expanded={registerExisting}
          >
            {registerExisting ? "▾" : "▸"} Register an existing instance
          </Button>

          {registerExisting ? (
            <div className="mt-3 flex flex-col gap-4 rounded-lg border border-border bg-background p-4">
              <p className="text-xs text-muted-foreground">
                For a laptop install or a break-glass <code>provision-instance.sh</code> box that
                already exists — the hub only records it here, it never provisions it.
              </p>

              <Field label="Kind" htmlFor="kind">
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger id="kind" className="w-full max-w-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="helm">Helm — a VM at {"<slug>"}.helm.nymbl.app</SelectItem>
                    <SelectItem value="local">Local — someone's laptop on loopback</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Agent token hash" htmlFor="agent-token-hash">
                <Input
                  id="agent-token-hash"
                  className="max-w-md"
                  value={agentTokenHash}
                  onChange={(e) => setAgentTokenHash(e.target.value)}
                  placeholder="64-character hex SHA-256 digest"
                  required={registerExisting}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Generate a token and its hash together, then keep the raw token to set as{" "}
                  <code>HELM_AGENT_TOKEN</code> on the instance itself:
                </p>
                <pre className="mt-1 max-w-xl overflow-x-auto rounded-md border bg-muted p-2 text-xs">
                  {'token=$(openssl rand -hex 32); echo "token: $token"\n' +
                    'echo -n "$token" | shasum -a 256 | cut -d" " -f1'}
                </pre>
              </Field>
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="block text-sm">
      <label htmlFor={htmlFor} className="mb-1 block font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}
